import React, { useMemo, useState } from 'react';
import {
    ResponsiveContainer,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    LineChart,
    Line,
} from 'recharts';

export type CashflowMonthlyRow = {
    month: string; // YYYY-MM

    // Plan (milestone.dueDate 기준)
    planIn: number; // CLIENT (받을 돈)
    planOut: number; // SUBCON (나갈 돈)

    // Actual (actualPayment.paidAt 기준)
    actualIn: number;
    actualOut: number;

    // Derived
    netPlan: number; // planIn - planOut
    netActual: number; // actualIn - actualOut

    // Cumulative (derived)
    cumPlanIn: number;
    cumPlanOut: number;
    cumActualIn: number;
    cumActualOut: number;
    cumNetPlan: number;
    cumNetActual: number;
};

const COLOR = {
    inPlan: '#93c5fd', // blue-300
    inActual: '#2563eb', // blue-600
    outPlan: '#fdba74', // orange-300
    outActual: '#f97316', // orange-500

    cumInPlan: '#60a5fa', // blue-400
    cumInActual: '#1d4ed8', // blue-700
    cumOutPlan: '#fb923c', // orange-400
    cumOutActual: '#ea580c', // orange-600
    cumNetActual: '#0f172a', // slate-900
};

type ChartType = 'MONTHLY' | 'NET' | 'CUMULATIVE';

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function fmtCompact(n: number) {
    const v = Number.isFinite(n) ? n : 0;
    // 숫자 자체를 보여주는 차트라 통화기호는 헤더에만 표기 (여러 통화 혼재 리스크 방지)
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(v);
}

function fmtTooltip(n: number) {
    const v = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    }).format(v);
}

function pillBtn(active: boolean) {
    return [
        'px-3 py-1.5 rounded-full text-xs font-medium transition',
        active ? 'bg-sky-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    ].join(' ');
}

function CustomTooltip({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: any[];
    label?: string;
}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border bg-white/95 shadow-sm px-3 py-2">
            <div className="text-[11px] text-gray-500 mb-1">{label}</div>
            <div className="space-y-1">
                {payload
                    .filter((p) => p?.value !== undefined && p?.value !== null)
                    .map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-6 text-xs">
                            <span className="text-gray-600">{p.name}</span>
                            <span className="font-semibold tabular-nums">{fmtTooltip(Number(p.value) || 0)}</span>
                        </div>
                    ))}
            </div>
        </div>
    );
}

function MonthlyInOutChart({ data }: { data: CashflowMonthlyRow[] }) {
    // Outflow는 음수로 내려가게 만들면 직관성이 좋아짐
    const view = useMemo(
        () =>
            data.map((r) => ({
                ...r,
                planOutNeg: -Math.abs(r.planOut || 0),
                actualOutNeg: -Math.abs(r.actualOut || 0),
            })),
        [data],
    );

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={view} barCategoryGap={16}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    {/* In */}
                    <Bar dataKey="planIn" name="수령 계획" fill={COLOR.inPlan} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actualIn" name="수령 실적" fill={COLOR.inActual} radius={[4, 4, 0, 0]} />

                    {/* Out (negative) */}
                    <Bar dataKey="planOutNeg" name="지급 계획" fill={COLOR.outPlan} radius={[0, 0, 4, 4]} />
                    <Bar dataKey="actualOutNeg" name="지급 실적" fill={COLOR.outActual} radius={[0, 0, 4, 4]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function NetChart({ data }: { data: CashflowMonthlyRow[] }) {
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} barCategoryGap={18}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="netPlan" name="순현금 계획" fill={COLOR.inPlan} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="netActual" name="순현금 실적" fill={COLOR.inActual} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function CumulativeChart({ data }: { data: CashflowMonthlyRow[] }) {
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    <Line type="monotone" dataKey="cumPlanIn" name="수령 누적 계획" stroke={COLOR.cumInPlan} strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="cumActualIn" name="수령 누적 실적" stroke={COLOR.cumInActual} strokeWidth={3} dot={false} />
                    <Line type="monotone" dataKey="cumPlanOut" name="지급 누적 계획" stroke={COLOR.cumOutPlan} strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="cumActualOut" name="지급 누적 실적" stroke={COLOR.cumOutActual} strokeWidth={3} dot={false} />
                    <Line
                        type="monotone"
                        dataKey="cumNetActual"
                        name="순현금 누적 실적"
                        stroke={COLOR.cumNetActual}
                        strokeWidth={4}
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default function CashflowChart({
    data,
    currency,
    title = 'Cashflow',
}: {
    data: CashflowMonthlyRow[];
    currency?: string;
    title?: string;
}) {
    const [type, setType] = useState<ChartType>('MONTHLY');

    const meta = useMemo(() => {
        if (!data.length) return { from: '—', to: '—' };
        return { from: data[0].month, to: data[data.length - 1].month };
    }, [data]);

    const total = useMemo(() => {
        // "현재까지"는 provided data 기준 (actual) 누적 마지막 값을 사용
        if (!data.length) {
            return {
                inPlan: 0,
                outPlan: 0,
                inActual: 0,
                outActual: 0,
            };
        }
        const last = data[data.length - 1];
        return {
            inPlan: last.cumPlanIn || 0,
            outPlan: last.cumPlanOut || 0,
            inActual: last.cumActualIn || 0,
            outActual: last.cumActualOut || 0,
        };
    }, [data]);

    const pctIn = useMemo(() => {
        const denom = total.inPlan || 0;
        if (denom <= 0) return 0;
        return clamp(Math.round((total.inActual / denom) * 100), 0, 999);
    }, [total]);

    const pctOut = useMemo(() => {
        const denom = total.outPlan || 0;
        if (denom <= 0) return 0;
        return clamp(Math.round((total.outActual / denom) * 100), 0, 999);
    }, [total]);

    return (
        <section className="rounded-2xl border bg-white overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                        {meta.from} → {meta.to}
                        {currency ? <span className="ml-1">· Unit: {currency}</span> : null}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className={pillBtn(type === 'MONTHLY')}
                        onClick={() => setType('MONTHLY')}
                    >
                        Monthly
                    </button>
                    <button type="button" className={pillBtn(type === 'NET')} onClick={() => setType('NET')}>
                        Net
                    </button>
                    <button
                        type="button"
                        className={pillBtn(type === 'CUMULATIVE')}
                        onClick={() => setType('CUMULATIVE')}
                    >
                        Cumulative
                    </button>
                </div>
            </div>

            {/* Mini stats */}
            <div className="px-4 py-3 border-b bg-gray-50/50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-xl border bg-white px-3 py-2">
                        <div className="text-[11px] text-gray-500">In Plan (Cum)</div>
                        <div className="text-sm font-semibold tabular-nums">{fmtTooltip(total.inPlan)}</div>
                    </div>
                    <div className="rounded-xl border bg-white px-3 py-2">
                        <div className="text-[11px] text-gray-500">In Actual (Cum)</div>
                        <div className="text-sm font-semibold tabular-nums">
                            {fmtTooltip(total.inActual)}{' '}
                            <span className="text-[11px] text-gray-500 font-medium">({pctIn}%)</span>
                        </div>
                    </div>
                    <div className="rounded-xl border bg-white px-3 py-2">
                        <div className="text-[11px] text-gray-500">Out Plan (Cum)</div>
                        <div className="text-sm font-semibold tabular-nums">{fmtTooltip(total.outPlan)}</div>
                    </div>
                    <div className="rounded-xl border bg-white px-3 py-2">
                        <div className="text-[11px] text-gray-500">Out Actual (Cum)</div>
                        <div className="text-sm font-semibold tabular-nums">
                            {fmtTooltip(total.outActual)}{' '}
                            <span className="text-[11px] text-gray-500 font-medium">({pctOut}%)</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="p-4">
                {!data.length ? (
                    <div className="text-sm text-gray-500 py-10 text-center">
                        표시할 데이터가 없습니다. (Milestone dueDate / Actual paidAt 기준)
                    </div>
                ) : (
                    <>
                        {type === 'MONTHLY' && <MonthlyInOutChart data={data} />}
                        {type === 'NET' && <NetChart data={data} />}
                        {type === 'CUMULATIVE' && <CumulativeChart data={data} />}
                    </>
                )}
            </div>
        </section>
    );
}
